import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Conversation, ConversationMessage } from "~/api/conversationTypes";
import { FACETS, isTurnMessage } from "~/api/conversationTypes";
import { MessageMarkdown } from "~/features/conversations/MessageMarkdown";
import { type MessageActions, MessageItem } from "~/features/conversations/transcript/MessageItem";
import type { StreamRow, StreamState } from "~/features/conversations/transcript/streamState";
import { streamPhase } from "~/features/conversations/transcript/streamState";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { cn } from "~/foundation/utils/cn";

/** Within this many pixels of the bottom, a new token scrolls the view. Further up, it does not. */
const STICK_TO_BOTTOM_PX = 64;

export type LaneFilter =
  | { type: "all" }
  | { type: "people" }
  | { type: "ai" }
  | { type: "participant"; id: string };

function laneMatches(message: ConversationMessage, lane: LaneFilter): boolean {
  switch (lane.type) {
    case "people":
      return message.authorType === "human";
    case "ai":
      return message.authorType !== "human";
    case "participant":
      return (
        (message.metadata.kind === "ai" && message.metadata.thinkerId === lane.id) ||
        message.author === lane.id
      );
    default:
      return true;
  }
}

function StreamingRow({ row }: { row: StreamRow }) {
  const { t } = useTranslation();
  return (
    <article
      className={cn(
        "flex flex-col gap-1.5 rounded-md border border-dashed p-2.5",
        row.kind === "moderator"
          ? "border-accent bg-accent-subtle"
          : "border-divider bg-bg-secondary",
      )}
    >
      <header className="flex flex-wrap items-center gap-2">
        <Text variant="small" className="font-medium text-text">
          {row.author || t("Assistant")}
        </Text>
        {row.kind === "moderator" ? (
          <span className="rounded-sm bg-accent-subtle px-1.5 py-0.5 text-caption text-accent">
            {t("Moderator")}
          </span>
        ) : null}
        {row.model ? <span className="text-caption text-text-tertiary">{row.model}</span> : null}
        {row.status === "streaming" ? <Spinner size={12} label={t("Answering")} /> : null}
      </header>

      {/* Skill cards render LIVE here, which kms could not do: it dropped both skill events. */}
      {row.skills.length > 0 ? (
        <ul className="flex flex-wrap gap-1">
          {row.skills.map((skill) => (
            <li
              key={`${row.id}-${skill.skillId}-${skill.query ?? ""}`}
              className="rounded-sm bg-bg px-1.5 py-0.5 text-caption text-text-tertiary"
            >
              {skill.status === "running"
                ? t("Running {{skill}}…", { skill: skill.skillId })
                : t("Ran {{skill}}", { skill: skill.skillId })}
            </li>
          ))}
        </ul>
      ) : null}

      {row.status === "empty" ? (
        <Text variant="small">{row.reason ?? t("This thinker had nothing to add.")}</Text>
      ) : row.status === "error" ? (
        <Text variant="small" className="text-danger">
          {row.reason ?? t("This thinker could not answer.")}
        </Text>
      ) : (
        <MessageMarkdown content={row.text} />
      )}
    </article>
  );
}

/**
 * The transcript (W8 §B.3).
 *
 * It reads the TURN face, which returns control rows as messages, and filters them here — the
 * drawer needs those rows for the context pack, the artifacts and the committed flag, so
 * fetching the filtered projection as well would be the expensive way to lose them.
 *
 * `mode` is the two views kms called Chat and Timeline: the same messages, with and without
 * the lane/facet chips and the control detail.
 */
export function Transcript({
  conversation,
  stream,
  actions,
  mode,
  messageExtras,
}: {
  conversation: Conversation;
  stream: StreamState;
  actions: MessageActions;
  mode: "chat" | "timeline";
  /**
   * S7 — what hangs off a finalised message: its skill cards and its sources. It is a slot
   * rather than a branch inside `MessageItem` because those two things need the DOCUMENT and
   * the editor ports, which a message row has no business knowing about.
   */
  messageExtras?: (message: ConversationMessage) => ReactNode;
}) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickRef = useRef(true);
  const [lane, setLane] = useState<LaneFilter>({ type: "all" });
  const [facet, setFacet] = useState<string>("");
  const [hidden, setHidden] = useState<string[]>([]);

  /** Hiding is a LOCAL decision — the rejection that reaches the server is the review write. */
  const itemActions = useMemo<MessageActions>(
    () => ({
      ...actions,
      onHide: (messageId: string) => {
        setHidden((current) => (current.includes(messageId) ? current : [...current, messageId]));
        actions.onHide(messageId);
      },
    }),
    [actions],
  );

  // W8-12: hidden ids are per conversation. kms leaked them between conversations.
  useEffect(() => {
    setHidden([]);
    setLane({ type: "all" });
    setFacet("");
  }, []);

  const turns = useMemo(
    () => conversation.messages.filter((message) => isTurnMessage(message.messageType)),
    [conversation.messages],
  );

  const visible = useMemo(
    () =>
      turns
        .filter((message) => laneMatches(message, lane))
        .filter((message) => (facet ? message.flags.facets.includes(facet) : true)),
    [turns, lane, facet],
  );

  const participants = useMemo(() => {
    const seen = new Map<string, string>();
    for (const message of turns) {
      if (message.metadata.kind === "ai" && message.metadata.thinkerId) {
        seen.set(message.metadata.thinkerId, message.author || message.metadata.thinkerId);
      }
    }
    return [...seen.entries()];
  }, [turns]);

  /** The rows still worth showing: a live row retires once its real message has landed. */
  const liveRows = stream.rows.filter(
    (row) => !row.settledId || !turns.some((message) => message.id === row.settledId),
  );

  /**
   * W8-13: only follow the stream when the reader is already at the bottom. kms scrolled on
   * every delta, so reading back through a long answer was impossible while it streamed.
   * The dependency is the streamed TEXT LENGTH, which is what actually changes per token.
   */
  const streamedLength = liveRows.reduce((total, row) => total + row.text.length, 0);
  useEffect(() => {
    const element = scrollRef.current;
    // The length IS the trigger — every streamed token changes it, and nothing else here does.
    if (!element || !stickRef.current || streamedLength < 0) {
      return;
    }
    element.scrollTop = element.scrollHeight;
  }, [streamedLength]);

  const onScroll = () => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    stickRef.current =
      element.scrollHeight - element.scrollTop - element.clientHeight < STICK_TO_BOTTOM_PX;
  };

  const phase = streamPhase(stream);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      {mode === "timeline" ? (
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap gap-1" role="group" aria-label={t("Lanes")}>
            {[
              { key: "all", label: t("All"), value: { type: "all" } as LaneFilter },
              { key: "people", label: t("People"), value: { type: "people" } as LaneFilter },
              { key: "ai", label: t("AIs"), value: { type: "ai" } as LaneFilter },
              ...participants.map(([id, name]) => ({
                key: id,
                label: name,
                value: { type: "participant", id } as LaneFilter,
              })),
            ].map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={() => setLane(chip.value)}
                className={cn(
                  "rounded-sm px-2 py-0.5 text-caption",
                  lane.type === chip.value.type &&
                    (chip.value.type !== "participant" ||
                      (lane.type === "participant" && lane.id === chip.key))
                    ? "bg-accent text-accent-text"
                    : "bg-bg-secondary text-text-secondary hover:bg-hover-bg",
                )}
              >
                {chip.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1" role="group" aria-label={t("Facets")}>
            {["", ...FACETS].map((entry) => (
              <button
                key={entry || "all-facets"}
                type="button"
                onClick={() => setFacet(entry)}
                className={cn(
                  "rounded-sm px-2 py-0.5 text-caption",
                  facet === entry
                    ? "bg-accent text-accent-text"
                    : "bg-bg-secondary text-text-secondary hover:bg-hover-bg",
                )}
              >
                {entry === "" ? t("All") : t(entry)}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto"
      >
        {visible.length === 0 && liveRows.length === 0 ? (
          <Empty message={t("No messages yet. Ask the first question.")} />
        ) : null}

        {visible.map((message) => (
          <div key={message.id} className="flex flex-col gap-1.5">
            <MessageItem
              message={message}
              actions={itemActions}
              hidden={hidden.includes(message.id)}
            />
            {hidden.includes(message.id) ? null : messageExtras?.(message)}
          </div>
        ))}

        {liveRows.map((row) => (
          <StreamingRow key={row.id} row={row} />
        ))}

        {stream.notices.length > 0 && mode === "timeline" ? (
          <ul className="flex flex-col gap-1">
            {stream.notices.map((notice) => (
              <li key={`${notice.kind}-${notice.text}`} className="text-caption text-text-tertiary">
                {notice.kind === "early_stop"
                  ? t("Stopped early: {{reason}}", { reason: notice.text })
                  : notice.kind === "skipped"
                    ? t("Skipped {{who}}", { who: notice.text })
                    : notice.kind === "unknown"
                      ? t("Unrecognised update: {{name}}", { name: notice.text })
                      : notice.text}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div aria-live="polite" className="min-h-4 text-caption text-text-tertiary">
        {stream.error ? (
          <span className="text-danger">{stream.error}</span>
        ) : phase === "thinking" ? (
          t("{{count}} thinkers are answering…", { count: stream.thinkerCount })
        ) : phase === "moderating" ? (
          t("The moderator is merging the answers…")
        ) : null}
      </div>

      {/* The hidden set is client-only, and saying so is the honest part. */}
      {hidden.length > 0 ? (
        <button
          type="button"
          onClick={() => setHidden([])}
          className="self-start text-caption text-link hover:underline"
        >
          {t("Show {{count}} hidden answers", { count: hidden.length })}
        </button>
      ) : null}
    </div>
  );
}
